import CheckboxesGroup from '@/entities/CheckboxesGroup/ui/CheckboxesGroup';
import RangeItem from '@/entities/RangeItem/ui/RangeItem';
import { getCatalogFilterState } from '@/shared/api/getCatalogFilterState';
import { ICatalogFilter } from '@/shared/api/getCatalogFilterState/types';
import { postCatalogFilterCount } from '@/shared/api/postCatalogFilterCount';
import { useCityStore } from '@/shared/store/cityStore';
import { useFiltersStore } from '@/shared/store/filtersStore';
import { Button } from '@/shared/ui/Button';
import CheckToggler from '@/shared/ui/CheckToggler/ui/CheckToggler';
import TogglerBadge from '@/shared/ui/TogglerBadge/ui/TogglerBadge';
import { groupBrandsByAlphabet } from '@/shared/utils/groupBrandsByAlphabet';
import { wordEnding } from '@/shared/utils/wordEnding';
import FiltersGroup from '@/widgets/FiltersGroup/ui/FiltersGroup';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import {
  cvaApplyButton,
  cvaBack,
  cvaBackIcon,
  cvaClose,
  cvaCloseIcon,
  cvaCloseIcon2,
  cvaCloseWrapper,
  cvaFooter,
  cvaForm,
  cvaGroup,
  cvaHeader,
  cvaHeaderTitle,
  cvaInner,
  cvaOverlay,
  cvaResetButton,
  cvaRoot,
  cvaScroll,
  cvaTitle,
} from './FiltersPopupStyles';
import IconArrowLeft from '/public/icons/arrow-left.svg';
import IconClose from '/public/icons/close.svg';
import IconClose2 from '/public/icons/close2.svg';

interface Props {
  onClose: () => void;
  filters: ICatalogFilter[];
  initialSectionId?: number;
  initialLabel?: number;
  isLoading?: boolean;
  setFilterStateForQuery: (value: any) => void;
}

const FiltersPopup: FC<Props> = ({
  onClose,
  filters,
  initialSectionId,
  initialLabel,
  isLoading,
  setFilterStateForQuery,
}) => {
  const [dynamicFilters, setDynamicFilters] = useState<ICatalogFilter[]>(filters);

  const setFilter = useFiltersStore((state) => state.setFilter);
  const setRangeFilter = useFiltersStore((state) => state.setRangeFilter);
  const filtersState = useFiltersStore((state) => state.filters);
  const filtersRangeState = useFiltersStore((state) => state.rangeFilters);

  const resetShopIds = useFiltersStore((state) => state.resetShopIds);

  const getSelectedFiltersCountByCode = useFiltersStore((state) => state.getSelectedFiltersCountByCode);
  const setShopId = useFiltersStore((state) => state.setShopId);
  const shopIds = useFiltersStore((state) => state.shop_ids);
  const resetFilterByCode = useFiltersStore((state) => state.resetFilterByCode);
  const resetFilters = useFiltersStore((state) => state.resetFilters);
  const resetSelectedShopsId = useFiltersStore((state) => state.resetSelectedShopsId);
  const getFilterStateBody = useFiltersStore((state) => state.getFilterBody);
  const setInitialSectionId = useFiltersStore((state) => state.setInitialSectionId);
  const setInitialLabelId = useFiltersStore((state) => state.setInitialLabelId);
  const cityShops = useCityStore((state) => state.shops);
  const getFilterShopIds = useFiltersStore((state) => state.getShopIds);
  const totalSelectedFilters = useFiltersStore((state) => state.getTotalSelectedFilters());
  // const cityHasShops = useCityStore((state) => state.cityHasShops);
  const [isLoadingShow, setIsLoadingShow] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showProductsCount, setShowProductsCount] = useState(0);

  const [expanded, setExpanded] = useState<false | number>(false);
  const handleSubmit = async () => {
    try {
      // todo
    } catch (e) {
      // console.error(e);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      disableBodyScroll(scrollRef.current, {
        reserveScrollBarGap: true,
      });
    }

    if (initialSectionId) {
      setInitialSectionId(initialSectionId);
    }
    if (initialLabel) {
      setInitialLabelId(initialLabel);
    }

    return () => {
      clearAllBodyScrollLocks();
      setInitialSectionId(null);
      setInitialLabelId(null);
    };
  }, []);

  const formInitialValues = dynamicFilters.reduce(
    (acc, filter) => {
      if (filter.type === 'checkbox') {
        acc[filter.code] = filtersState[filter.code] || [];
      } else if (filter.type === 'minmax') {
        acc[filter.code] = filtersState[filter.code] || {
          min: filter.min || 0,
          max: filter.max || 0,
        };
      } else {
        acc[filter.code] = filtersState[filter.code] || null;
      }
      return acc;
    },
    {
      availability: [],
      expressDelivery: [],
    } as Record<string, any>
  );

  const handleCheckboxGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked, name } = e.target;

    if (name === 'SHOPS') {
      const { value, checked } = e.target;

      if (getFilterShopIds().length > 15) {
        resetShopIds();
      }

      setShopId(Number(value), checked);
      setFilterStateForQuery(getFilterStateBody());
    } else {
      setFilter(name, value, checked);
    }
  };

  // const {
  //   data: allShops,
  //   isFetching: isLoading,
  // } = useQuery({
  //   queryKey: ['allShops'],
  //   queryFn: () => {
  //     const res = getShops();
  //     return res;
  //   },
  // });

  const allShopsIds = useCityStore((state) => state.allStoresIds);

  const handleCheckboxAvailabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    const curIds = getFilterShopIds();

    if (isLoading) return;

    if (checked) {
      allShopsIds?.map((id) => {
        if (curIds.includes(id)) return id;
        setShopId(id, true);
        return id;
      });
    } else {
      allShopsIds?.map((id) => {
        setShopId(id, false);
        return id;
      });
    }
  };

  // const handleCheckboxExpressDeliveryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const { checked } = e.target;
  //   if (checked) {
  //     setFilter('expressDelivery', '1', true);
  //   } else {
  //     setFilter('expressDelivery', '1', false);
  //   }
  // };

  const handleRangeChange = (values: number[], name: string) => {
    setRangeFilter(name, {
      min: values[0],
      max: values[1],
    });
  };

  const handleCheckboxReset = (code: string) => {
    if (code === 'SHOPS') {
      resetSelectedShopsId();
      setFilterStateForQuery(getFilterStateBody());
      return;
    }

    resetFilterByCode(code);

    if (code === 'SECTION' && initialSectionId) {
      setFilter('SECTION', `${initialSectionId}`, true);
    }

    if (code === 'LABEL' && initialLabel) {
      setFilter('LABEL', `${initialLabel}`, true);
    }
  };

  const [isReady, cancel] = useDebounce(
    async () => {
      setIsLoadingShow(true);
      const result = await postCatalogFilterCount(getFilterStateBody());
      setShowProductsCount(result);
      setIsLoadingShow(false);
    },
    300,
    [filtersState, filtersRangeState, shopIds]
  );

  useEffect(() => {
    if (isReady() !== null) {
      isReady();
    }
    return () => {
      cancel();
    };
  }, [filtersState, filtersRangeState]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const listShops = cityShops?.map((shop) => ({
      code: shop.name,
      value: shop.store_id,
      count: '1',
      isDisabled: false,
    }));

    const shopsFilter = {
      code: 'SHOPS',
      title: 'Наличие в магазинах',
      type: 'checkbox',
      values: listShops,
    };
    getCatalogFilterState(getFilterStateBody()).then((res) => {
      if (res.filter) {
        setDynamicFilters([...res.filter, shopsFilter]);
      }
    });
  }, [filtersState, filtersRangeState, cityShops]);

  return (
    <motion.div
      className={cvaRoot()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      data-lenis-prevent
    >
      <div className={cvaOverlay()} onClick={() => onClose()}></div>
      <Button
        type="button"
        className={cvaClose()}
        rootWrapperClassName={cvaCloseWrapper()}
        onClick={() => onClose()}
        color={'white'}
        hoverEffect={'highlight'}
        disableInViewAnimation
      >
        <IconClose className={cvaCloseIcon()} />
        <IconClose2 className={cvaCloseIcon2()} />
      </Button>
      <Button type="button" className={cvaBack()} onClick={() => onClose()} color={'white'} hoverEffect={'highlight'}>
        <IconArrowLeft className={cvaBackIcon()} />
      </Button>
      <div className={cvaInner()}>
        <Formik initialValues={formInitialValues} enableReinitialize onSubmit={handleSubmit}>
          {({ setFieldValue }) => (
            <Form className={cvaForm()} autoComplete="off">
              <div className={cvaHeader()}>
                <div className={cvaHeaderTitle()}>Фильтры</div>
              </div>
              <div className={cvaScroll()} ref={scrollRef}>
                <div className={cvaTitle()}>Фильтры</div>
                <div className={cvaGroup()}>
                  <CheckToggler
                    name={'availability'}
                    value="true"
                    checked={getFilterShopIds()?.length === allShopsIds?.length}
                    onChange={handleCheckboxAvailabilityChange}
                  >
                    В наличии
                  </CheckToggler>
                  {/* {cityHasShops ? (
                    <CheckToggler
                      name={'expressDelivery'}
                      value="true"
                      checked={
                        filtersState.expressDelivery !== undefined &&
                        filtersState.expressDelivery &&
                        filtersState.expressDelivery.length > 0
                      }
                      onChange={handleCheckboxExpressDeliveryChange}
                    >
                      Экспресс-доставка
                    </CheckToggler>
                  ) : null} */}
                </div>
                {dynamicFilters?.map((filter, idx) =>
                  (filter.values?.length &&
                    filter.values?.length > 0 &&
                    filter.code !== 'NAZNACHENIE' &&
                    filter.values?.filter((i) => !i.is_disabled)?.length) ||
                  (filter.type === 'minmax' && filter.min && filter.max) ? (
                    <FiltersGroup
                      key={idx}
                      i={idx}
                      header={
                        filter.type === 'minmax'
                          ? `${filter.title}, ₽ ${filtersRangeState[filter.code]?.min && filtersRangeState[filter.code]?.max ? `<span>${filtersRangeState[filter.code]?.min} — ${filtersRangeState[filter.code]?.max}</span>` : ''}`
                          : filter.title
                      }
                      expanded={expanded}
                      setExpanded={setExpanded}
                      forceShow={false}
                      count={
                        filter.type === 'checkbox'
                          ? getSelectedFiltersCountByCode(filter.code)
                          : filter.type === 'minmax' && filtersRangeState[filter.code]?.min
                            ? 1
                            : 0
                      }
                      hasCount={filter.type !== 'minmax'}
                      hasReset
                      onReset={handleCheckboxReset}
                      code={filter.code}
                    >
                      {filter.type === 'checkbox' && filter.code !== 'LABEL' ? (
                        <CheckboxesGroup
                          searchTitle={
                            filter.code === 'BREND'
                              ? 'Найти бренд'
                              : filter.code === 'SHOPS'
                                ? 'Наличие в магазинах'
                                : ''
                          }
                          name={filter.code}
                          items={
                            filter.values
                              ?.filter((i) => !i.is_disabled)
                              ?.map((value, idx) => ({
                                id: idx,
                                title: value.code,
                                value: value.value,
                              })) || []
                          }
                          fullItems={
                            filter.code === 'BREND'
                              ? groupBrandsByAlphabet(filter.values?.filter((i) => !i.is_disabled) ?? [])
                              : []
                          }
                          onChange={handleCheckboxGroupChange}
                        />
                      ) : null}
                      {filter.code === 'LABEL' ? (
                        <>
                          {filter.values?.map((value, idx) => {
                            return initialLabel && `${initialLabel}` === `${value.value}` ? null : (
                              <CheckToggler
                                checked={filtersState[filter.code]?.includes(`${value.value}`)}
                                name={filter.code}
                                value={value.value}
                                key={idx}
                                onChange={handleCheckboxGroupChange}
                              >
                                <TogglerBadge
                                  type={
                                    value.code === 'Набор'
                                      ? '1+1'
                                      : value.code === 'Бестселлеры'
                                        ? 'hit'
                                        : value.code === 'Новинка'
                                          ? 'new'
                                          : 'sale'
                                  }
                                />
                              </CheckToggler>
                            );
                          })}
                        </>
                      ) : null}
                      {filter.type === 'minmax' ? (
                        <RangeItem
                          from={filtersRangeState[filter.code]?.min || filter.min || 0}
                          to={filtersRangeState[filter.code]?.max || filter.max || 10}
                          min={filter.min || 0}
                          max={filter.max || 10}
                          onChange={(values) => {
                            handleRangeChange(values, filter.code);
                          }}
                        />
                      ) : null}
                    </FiltersGroup>
                  ) : null
                )}
              </div>
              <div className={cvaFooter()}>
                <Button
                  type="button"
                  color={'mediumGreen'}
                  size={'medium'}
                  className={cvaApplyButton()}
                  rootWrapperClassName="w-full"
                  animateWrapperClassName="w-full"
                  onClick={() => {
                    onClose();
                    setFilterStateForQuery(getFilterStateBody());
                  }}
                  hoverEffect={'highlight'}
                  isLoading={isLoadingShow}
                  disableInViewAnimation
                >
                  {showProductsCount ? (
                    <>
                      Показать {showProductsCount + ' '}
                      {wordEnding(showProductsCount, ['товар', 'товара', 'товаров'])}
                    </>
                  ) : (
                    'Показать'
                  )}
                </Button>
                {totalSelectedFilters ? (
                  <Button
                    type="button"
                    size={'medium'}
                    className={cvaResetButton()}
                    rootWrapperClassName="w-full"
                    animateWrapperClassName="w-full"
                    onClick={() => {
                      resetFilters();
                      if (initialSectionId) {
                        setFilter('SECTION', `${initialSectionId}`, true);
                      }
                      if (initialLabel) {
                        setFilter('LABEL', `${initialLabel}`, true);
                      }
                      setFieldValue('availability', []);
                      setFieldValue('expressDelivery', []);
                    }}
                  >
                    Сбросить фильтры
                  </Button>
                ) : null}
              </div>
              <div id="popup-filters"></div>
            </Form>
          )}
        </Formik>
      </div>
    </motion.div>
  );
};

export default FiltersPopup;
